/**
 * O que vale para TODO endpoint (spec 03 §1).
 *
 * Envelope de erro, paginação e os tipos primitivos que a spec fixa. Estar aqui, e
 * não em cada módulo, é o que faz "a tela lê o envelope" ser verdade em vez de
 * intenção: quem escreve uma tela nova importa daqui e ganha o formato certo.
 */
import { z } from 'zod'

/** spec 03 §1.4 — o envelope é UM. */
export const codigoDeErro = z.enum([
  'VALIDATION',
  'NOT_FOUND',
  'FORBIDDEN',
  'CONFLICT',
  'BUSINESS_RULE',
  'RATE_LIMIT',
  'UNAVAILABLE',
  'INTERNAL',
])
export type CodigoDeErro = z.infer<typeof codigoDeErro>

export const detalheDeErro = z.object({ path: z.string(), rule: z.string() })
export type DetalheDeErro = z.infer<typeof detalheDeErro>

export const envelopeDeErro = z.object({
  error: z.object({
    code: codigoDeErro,
    message: z.string(),
    details: z.array(detalheDeErro).optional(),
  }),
})
export type EnvelopeDeErro = z.infer<typeof envelopeDeErro>

/** spec 03 §1.2 — toda lista responde assim. */
export const meta = z.object({ page: z.number().int(), limit: z.number().int(), total: z.number().int() })

export function lista<T extends z.ZodType>(item: T) {
  return z.object({ data: z.array(item), meta })
}

export const uuid = z.uuid({ version: 'v7' })
/**
 * O uuid como a API o ACEITA em rota.
 *
 * Aceitar qualquer versão na entrada e gerar só v7 é deliberado: as chaves nascem
 * `uuidv7()` no banco (spec 01 §8.3), mas recusar um v4 na URL transformaria um
 * "não encontrado" comum em erro de validação — e contaria ao curioso que o
 * formato dele estava certo, só o registro é que não existia.
 */
export const uuidDeRota = z.uuid()

/**
 * A página do seletor com busca no servidor (item 03 · DS-529).
 *
 * A tela pede 20 e o servidor aceita no máximo 50. O teto existe porque o `limit` chega
 * pela URL: sem ele, `?limit=100000` devolveria o cadastro inteiro que o seletor existe
 * para não baixar. Sem `limit`, cada rota responde como antes (a 3.1 pagina no cliente).
 */
export const LIMITE_PADRAO_DO_SELETOR = 20
export const LIMITE_MAXIMO_DO_SELETOR = 50

/** O `?limit=` do seletor: texto na URL, inteiro de 1 a 50 no servidor. Fora disso, 400. */
export const limiteDoSeletor = z.coerce.number().int().min(1).max(LIMITE_MAXIMO_DO_SELETOR)

/**
 * Uma lista de ids na URL — `?excluir=a,b`, `?chaves=a`, `?capacidades=a`.
 *
 * Vazio, vírgula solta e item malformado são 400, nunca "sem filtro": uma lista que
 * falhasse em silêncio devolveria o recorte inteiro no lugar do recorte pedido, que é
 * o mesmo erro que o AB-204 fechou na exportação.
 */
export const listaDeUuidsNaUrl = z
  .string()
  .transform((texto) => texto.split(',').map((parte) => parte.trim()))
  .pipe(z.array(uuidDeRota).min(1).max(LIMITE_MAXIMO_DO_SELETOR))

/**
 * A chave da INTENÇÃO humana, para idempotência de comando financeiro (lote D1).
 *
 * O cliente a gera na abertura do modal — uma intenção, uma chave — e a REUSA através
 * da falha de rede. Dois cliques deliberados são duas intenções e, portanto, dois
 * pagamentos: nenhuma heurística de valor e data deve juntá-los.
 *
 * **Qualquer versão de uuid**, não só v7: quem a gera é o navegador, com
 * `crypto.randomUUID()`, que produz v4. Exigir v7 aqui recusaria a chave de todo
 * cliente — a regra do v7 é para as chaves que NASCEM no banco (spec 01 §8.3).
 *
 * `.optional()` nos contratos durante a fase "observar" (a flag
 * `DV_EXIGIR_CHAVE_IDEMPOTENTE` é que fecha a porta), para que o binário N-1 continue
 * funcionando na janela de expand.
 */
export const chaveDeComando = z.uuid()

/** spec 01 §8.2 — data nominal é string, nunca `Date`. */
export const dataNominal = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use uma data no formato AAAA-MM-DD.')

/** ISO 4217 e ISO 3166-1 alpha-2, como no schema (spec 02 §1.5 e §2.2d). */
export const codigoDeMoeda = z.string().length(3).regex(/^[A-Z]{3}$/)
export const codigoDePais = z.string().length(2).regex(/^[A-Z]{2}$/)

/**
 * E-mail — a MESMA regra no formulário e no servidor.
 *
 * A tela 1.1 valida antes do POST ("Informe um e-mail válido.") e a API valida de
 * novo. Duas implementações dessa regra dariam, mais cedo ou mais tarde, dois
 * veredictos para o mesmo texto digitado.
 */
export const email = z.email('Informe um e-mail válido.')

/**
 * Política de senha (spec 04-TELAS/1.1 e 2.3): **mínimo 8**.
 *
 * Vale na DEFINIÇÃO e na TROCA — nunca na validação do login, onde a senha é apenas
 * obrigatória (exigir tamanho ali contaria ao atacante qual senha nem vale a pena
 * tentar). A recomendação de misturar letras, números e símbolos é microcopy, não
 * regra: bloquear por composição empurra para senha pior e mais anotada.
 */
export const MINIMO_DA_SENHA = 8
export const senha = z
  .string()
  .min(MINIMO_DA_SENHA, `A senha deve ter ao menos ${String(MINIMO_DA_SENHA)} caracteres.`)

/**
 * Percentual é FRAÇÃO no fio (princípio 7): de 0 a 1, em texto decimal, sem sinal.
 *
 * Uma regra só para a comissão (AB-023) e para os percentuais dos catálogos (DS-542): a
 * tela mostra de 0 a 100, e o que viaja e se grava é a fração. `1.000000` e `0.100000`
 * passam, porque é assim que o `dv_pct` volta do banco e o Desfazer o reenvia.
 */
export const FRACAO_ENTRE_ZERO_E_UM = /^(?:0(?:\.\d+)?|1(?:\.0+)?)$/

/** Comissão: percentual de 0–100% na tela; fração de 0–1 no fio (spec 4.3 / 10.4). */
export const fracaoDeComissao = z.string().regex(
  FRACAO_ENTRE_ZERO_E_UM,
  'Informe um percentual entre 0 e 100 (fração entre 0 e 1 na API).',
)

/**
 * Dinheiro de entrada em texto decimal CANÔNICO (`1500.00`, `1500.5`, `0`): sem milhar, sem vírgula,
 * até duas casas. A tela normaliza o que a pessoa digitou (`normalizarValor`); o contrato só aceita o
 * resultado. `1.500` aqui é recusado, e não lido como R$ 1,50: o ponto com três casas não é canônico.
 */
export const dinheiroPositivo = z.string().regex(
  /^(?=\d*(?:\.\d{1,2})?$)(?=.*[1-9])\d{1,16}(?:\.\d{1,2})?$/,
  'Use valor decimal positivo.',
)
export const dinheiroNaoNegativo = z.string().regex(/^\d{1,16}(?:\.\d{1,2})?$/, 'Use valor decimal não negativo.')
